import { z } from 'zod';
import { parseBoolEnv } from '~lib';
import { isFlagOn } from './lib/edge-gate';

/**
 * Valid Datadog site domains
 */
const DatadogSites = [
  'datadoghq.com',
  'datadoghq.eu',
  'us3.datadoghq.com',
  'us5.datadoghq.com',
  'ddog-gov.com',
  'ap1.datadoghq.com',
] as const;

type DatadogSite = (typeof DatadogSites)[number];

/**
 * Valid log levels for Datadog browser logging
 */
export const LogLevels = ['debug', 'info', 'warn', 'error'] as const;
export type LogLevel = (typeof LogLevels)[number];

export type AppEnv = {
  backendApiUrl: string;
  /** @desc Conversation socket origin (wave-2 broker host); backend origin when unset. */
  wsUrl?: string | undefined;
  workosClientId: string;
  workosApiHostname?: string | undefined;
  isLocal: boolean;
  accessEnforcement: boolean;
  /** @desc Round 3 lane 11: the admin-only Restricted section (`VITE_RESTRICTED_NAV`), staging only. */
  restrictedNav: boolean;
  /** @desc H2-7: the admin Automations surfaces and their live-update rooms (`VITE_AUTOMATIONS_ENABLED`). */
  automationsEnabled: boolean;
  /**
   * @desc H2-403 (D20): seconds without a chat event before a running turn shows "Still working"
   * (`VITE_AUTOMATIONS_STALL_SECONDS`, default 120: model calls of 79 s and 106 s are healthy).
   */
  automationsStallSeconds: number;
  datadog: {
    applicationId: string;
    clientToken: string;
    service: string;
    site: DatadogSite;
    env: string;
    loggingEnabled: boolean;
    logLevel: LogLevel;
  };
  render: {
    modelThinking: boolean;
    taskDetails: boolean;
  };
  sse: {
    heartbeatTimeoutSeconds: number;
  };
  orchestration: {
    maxActiveConversations: number;
    enableGarbageCollection: boolean;
  };
  storage: {
    disableLocalStorageCache: boolean;
  };
  cookieConsent: {
    /** @desc `Domain` attribute of the shared consent cookie; unset means host-only. */
    domain?: string | undefined;
  };
};

const appEnvSchema = z.object({
  isLocal: z.boolean(),
  /**
   * @desc H2-171 per-deployment access guards (`VITE_ACCESS_ENFORCEMENT_ENABLED`). Off until the
   * WorkOS role backfill has given every membership explicit per-product roles; flipped in step
   * with the backend's `ACCESS_ENFORCEMENT_ENABLED`, backend first. See `lib/access.ts`.
   */
  accessEnforcement: z.boolean().default(false),
  /**
   * @desc The Restricted section (round 3, lane 11, H2-314): the admin-only "Restricted" nav group
   * and `/restricted/*` routes. `VITE_RESTRICTED_NAV="1"` turns it on; set only on the staging
   * environment of the `h2gc-portal` Vercel project (exxo-infra `projects/h2gc-vercel/data.ts`),
   * never on production. Parsed with the edge gate's `isFlagOn`, because the edge middleware reads
   * the same variable at runtime and the two must agree.
   */
  restrictedNav: z.boolean().default(false),
  /**
   * @desc Admin automations (H2-7, `VITE_AUTOMATIONS_ENABLED`): the Automations nav group (task
   * 8.1) and the live-update room membership behind it (`agents/automations-live.ts`). Off by
   * default; admin-only regardless, since every automations procedure and room checks again.
   */
  automationsEnabled: z.boolean().default(false),
  backendApiUrl: z.url(),
  /**
   * @desc Conversation event socket origin (`VITE_WS_URL`). Unset means the socket rides the
   * backend origin — today's in-process socket server. Set at the H2-172 wave-2 cutover to
   * the broker host (ws.<env>.h2gc.exxo.dev).
   */
  wsUrl: z.url().optional(),
  workosClientId: z.string().min(1),
  workosApiHostname: z.string().min(1).optional(),
  datadog: z.object({
    applicationId: z.string().min(1),
    clientToken: z.string().min(1),
    service: z.string().min(1),
    site: z.enum(DatadogSites),
    env: z.string().min(1),
    loggingEnabled: z.boolean().default(true),
    logLevel: z.enum(LogLevels).default('info'),
  }),
  render: z.object({
    modelThinking: z.boolean(),
    taskDetails: z.boolean(),
  }),
  automationsStallSeconds: z.coerce.number().int().min(10).max(3600).default(120),
  sse: z.object({
    heartbeatTimeoutSeconds: z.coerce.number().int().min(10).max(600).default(120),
  }),
  orchestration: z.object({
    maxActiveConversations: z.coerce.number().int().min(1).max(50).default(10),
    enableGarbageCollection: z.boolean().default(false),
  }),
  storage: z.object({
    disableLocalStorageCache: z.boolean().default(false),
  }),
  /**
   * @desc H2-204: `VITE_COOKIE_CONSENT_DOMAIN` is `.h2gc.com` on production so the consent cookie
   * written by h2gc.com's banner is the one this app reads (and vice versa), and a visitor
   * redirected from the legacy site is not asked twice. Unset elsewhere — staging lives on
   * another apex, and a host-only cookie is the right default for local dev.
   */
  cookieConsent: z.object({
    domain: z
      .string()
      .regex(/^\.[a-z0-9-]+(\.[a-z0-9-]+)+$/i, 'a leading-dot registrable domain, e.g. .h2gc.com')
      .optional(),
  }),
});

function parseAppEnv(): AppEnv {
  const datadog = {
    applicationId: import.meta.env.VITE_DD_APPLICATION_ID,
    clientToken: import.meta.env.VITE_DD_CLIENT_TOKEN,
    service: import.meta.env.VITE_DD_SERVICE,
    site: import.meta.env.VITE_DD_SITE,
    env: import.meta.env.VITE_DD_ENV,
    loggingEnabled: parseBoolEnv(import.meta.env.VITE_DD_LOGGING_ENABLED),
    logLevel: import.meta.env.VITE_DD_LOG_LEVEL,
  };

  const isLocal = parseBoolEnv(import.meta.env.VITE_IS_LOCAL);

  const common = {
    isLocal,
    accessEnforcement: parseBoolEnv(import.meta.env.VITE_ACCESS_ENFORCEMENT_ENABLED),
    restrictedNav: isFlagOn(import.meta.env.VITE_RESTRICTED_NAV),
    automationsEnabled: isFlagOn(import.meta.env.VITE_AUTOMATIONS_ENABLED),
    automationsStallSeconds: import.meta.env.VITE_AUTOMATIONS_STALL_SECONDS || undefined,
    workosClientId: import.meta.env.VITE_WORKOS_CLIENT_ID,
    workosApiHostname: import.meta.env.VITE_WORKOS_API_HOSTNAME || undefined,
    datadog,
    sse: {
      heartbeatTimeoutSeconds: import.meta.env.VITE_SSE_HEARTBEAT_TIMEOUT_SECONDS,
    },
    orchestration: {
      maxActiveConversations: import.meta.env.VITE_ORCHESTRATION_MAX_ACTIVE,
      enableGarbageCollection: parseBoolEnv(import.meta.env.VITE_ORCHESTRATION_ENABLE_GC),
    },
    storage: {
      disableLocalStorageCache: parseBoolEnv(import.meta.env.VITE_DISABLE_LOCALSTORAGE_CACHE),
    },
    cookieConsent: {
      domain: import.meta.env.VITE_COOKIE_CONSENT_DOMAIN || undefined,
    },
  };

  if (isLocal) {
    return appEnvSchema.parse({
      ...common,
      backendApiUrl: import.meta.env.VITE_BACKEND_API_URL || 'http://localhost:8080',
      wsUrl: import.meta.env.VITE_WS_URL || undefined,
      render: {
        modelThinking: parseBoolEnv(import.meta.env.VITE_RENDER_MODEL_THINKING) ?? true,
        taskDetails: parseBoolEnv(import.meta.env.VITE_RENDER_TASK_DETAILS) ?? true,
      },
    });
  }

  return appEnvSchema.parse({
    ...common,
    backendApiUrl: import.meta.env.VITE_BACKEND_API_URL,
    wsUrl: import.meta.env.VITE_WS_URL || undefined,
    render: {
      modelThinking: parseBoolEnv(import.meta.env.VITE_RENDER_MODEL_THINKING),
      taskDetails: parseBoolEnv(import.meta.env.VITE_RENDER_TASK_DETAILS),
    },
  });
}

let appEnv: AppEnv;

export function getAppEnv(): AppEnv {
  if (appEnv) return appEnv;
  appEnv = parseAppEnv();
  return appEnv;
}
